import React, { useEffect, useState, useMemo } from 'react';
import { MessageCircle, Search, Loader2, ChevronRight, Lock, ExternalLink, Filter, ArrowUpDown } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { adminListThreads, getOfferThread } from '../services/api';
import Pagination from './Pagination';

const PAGE_SIZE = 20;

// Time-range presets for the "Most recent" filter. Values are in HOURS
// (null means unbounded). Sorted from tightest to loosest so admins can
// quickly zoom into today / this week / this month.
const RANGE_PRESETS = [
  { key: 'all',   label: 'All time',       hours: null },
  { key: '24h',   label: 'Last 24 hours',  hours: 24 },
  { key: '7d',    label: 'Last 7 days',    hours: 24 * 7 },
  { key: '30d',   label: 'Last 30 days',   hours: 24 * 30 },
];

// Sort options — determines which end of the (filtered) list bubbles to
// the top. "Most recent" mirrors the natural product default; "Oldest first"
// helps admins triage long-stale threads that never got a reply.
const SORT_OPTIONS = [
  { key: 'newest', label: 'Most recent' },
  { key: 'oldest', label: 'Oldest first' },
];

/**
 * Admin "Conversations" tab — read-only list of all bid threads with search
 * by JD ref / customer / driver / message text. Click a row to open the full
 * thread in a modal.
 *
 * Backed by:
 *   GET /api/admin/threads?q={...}
 *   GET /api/offers/{offerId}/thread
 */
export default function AdminConversations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rangeKey, setRangeKey] = useState('all');
  const [sortKey, setSortKey] = useState('newest');
  const [page, setPage] = useState(0);
  const [openOfferId, setOpenOfferId] = useState(null);
  const [openThread, setOpenThread] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchRows = async () => {
      setLoading(true);
      try {
        const { data } = await adminListThreads(search.trim() || undefined);
        if (!cancelled) {
          setRows(Array.isArray(data) ? data : []);
          setPage(0); // reset to first page whenever the search changes
        }
      } catch (e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const t = setTimeout(fetchRows, search ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  // Client-side ordering + time-range filter. Backend already sorts by
  // `thread_updated_at` desc, but this is a defensive re-sort in case any
  // row is missing that field (bid with no reply yet) so it never bubbles
  // to the top ahead of a genuinely more recent conversation. The
  // "Most recent" range preset filters everything older than the cutoff.
  const filteredRows = useMemo(() => {
    const preset = RANGE_PRESETS.find(r => r.key === rangeKey) || RANGE_PRESETS[0];
    const cutoffTs = preset.hours ? Date.now() - preset.hours * 3600 * 1000 : null;
    const rowTs = (r) => {
      const raw = r.last_message_at || r.bid_created_at;
      const t = raw ? Date.parse(raw) : NaN;
      return Number.isFinite(t) ? t : 0;
    };
    return rows
      .filter(r => cutoffTs === null || rowTs(r) >= cutoffTs)
      .slice()
      .sort((a, b) => sortKey === 'oldest' ? rowTs(a) - rowTs(b) : rowTs(b) - rowTs(a));
  }, [rows, rangeKey, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const visibleRows = useMemo(
    () => filteredRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filteredRows, page]
  );

  // Reset to the first page whenever the range or sort changes so admins
  // don't land on an out-of-bounds page for the newly-filtered dataset.
  useEffect(() => { setPage(0); }, [rangeKey, sortKey]);

  const openConversation = async (offerId) => {
    setOpenOfferId(offerId);
    setOpenLoading(true);
    try {
      const { data } = await getOfferThread(offerId);
      setOpenThread(data);
    } catch (e) {
      setOpenThread(null);
    } finally {
      setOpenLoading(false);
    }
  };

  const closeConversation = () => {
    setOpenOfferId(null);
    setOpenThread(null);
  };

  return (
    <Card className="border border-gray-200 shadow-none" data-testid="admin-conversations-panel">
      <CardContent className="py-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#1B5E20]" />
            <div>
              <h3 className="text-base font-bold text-[#050505] font-['Inter']">Conversations</h3>
              <p className="text-xs text-gray-500">All bid threads between customers and drivers. Read-only.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
            {/* Time range — "Most recent" filter */}
            <div className="relative">
              <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <select
                value={rangeKey}
                onChange={(e) => setRangeKey(e.target.value)}
                className="pl-7 pr-6 h-9 text-sm border border-gray-200 rounded-md bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30"
                data-testid="admin-conversations-range"
                aria-label="Filter conversations by recency"
              >
                {RANGE_PRESETS.map(r => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>
            {/* Sort order — most recent (default) or oldest first */}
            <div className="relative">
              <ArrowUpDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                className="pl-7 pr-6 h-9 text-sm border border-gray-200 rounded-md bg-white appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30"
                data-testid="admin-conversations-sort"
                aria-label="Sort conversations by recency"
              >
                {SORT_OPTIONS.map(o => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="relative flex-1 sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by JD ref, name, or text…"
                className="pl-9 h-9 rounded-md text-sm"
                data-testid="admin-conversations-search"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading conversations…
          </div>
        ) : filteredRows.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-10" data-testid="admin-conversations-empty">
            {rows.length === 0
              ? 'No conversations yet.'
              : `No conversations in the selected time range${search ? ' or matching your search' : ''}.`}
          </p>
        ) : (
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-md overflow-hidden" data-testid="admin-conversations-list">
            {visibleRows.map((r) => (
              <button
                key={r.offer_id}
                type="button"
                onClick={() => openConversation(r.offer_id)}
                className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors"
                data-testid={`admin-conv-row-${r.offer_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap text-sm">
                    <span className="font-semibold text-[#1B5E20]">{r.delivery_ref || '—'}</span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-700">{r.customer_name || 'Customer'} ↔ {r.driver_name || 'Driver'}</span>
                    <span className="ml-auto text-xs text-gray-400 tabular-nums">{r.message_count} msg{r.message_count === 1 ? '' : 's'}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-1">"{r.preview || '(no preview)'}"</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    {r.last_message_at ? new Date(r.last_message_at).toLocaleString() : ''}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 mt-2 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}

        {/* Pagination + result summary */}
        {!loading && rows.length > 0 && (
          <div className="mt-4 flex flex-col items-center gap-2">
            <p className="text-[11px] text-gray-400" data-testid="admin-conv-pagination-summary">
              Showing {page * PAGE_SIZE + 1}–{Math.min(filteredRows.length, (page + 1) * PAGE_SIZE)} of {filteredRows.length}{rangeKey !== 'all' ? ` (of ${rows.length} total)` : ''}
            </p>
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={setPage}
              dataTestId="admin-conv-pagination"
            />
          </div>
        )}
      </CardContent>

      {/* Conversation viewer */}
      <Dialog open={!!openOfferId} onOpenChange={(v) => !v && closeConversation()}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Inter']">
              {openThread?.delivery_ref ? `Conversation · ${openThread.delivery_ref}` : 'Conversation'}
            </DialogTitle>
          </DialogHeader>

          {openLoading || !openThread ? (
            <div className="flex items-center justify-center py-10 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-thread-modal-body">
              <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                <p><span className="font-semibold text-gray-700">Customer:</span> {openThread.customer_name || '—'}</p>
                <p><span className="font-semibold text-gray-700">Driver:</span> {openThread.driver_name || '—'}</p>
                <p className="flex items-center gap-1 mt-1">
                  <Lock className="w-3 h-3" />
                  Stage: {openThread.post_payment ? 'Post-payment (free-form)' : 'Pre-payment (limited)'}
                </p>
              </div>

              <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="admin-thread-messages">
                {openThread.bid_message && (
                  <ThreadRow
                    senderRole="driver"
                    senderName={openThread.driver_name || 'Driver'}
                    text={openThread.bid_message}
                    createdAt={openThread.bid_created_at}
                    badge="Bid message"
                  />
                )}
                {(openThread.messages || []).map((m, i) => (
                  <ThreadRow
                    key={i}
                    senderRole={m.sender_role}
                    senderName={m.sender_name}
                    text={m.text}
                    createdAt={m.created_at}
                  />
                ))}
              </div>

              <p className="text-[11px] text-gray-400 italic flex items-center gap-1 pt-2 border-t border-gray-100">
                <ExternalLink className="w-3 h-3" /> Admin view is read-only. Use account suspension if abuse is found.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ThreadRow({ senderRole, senderName, text, createdAt, badge }) {
  const sideClass = senderRole === 'driver' ? 'bg-emerald-50 border-emerald-100' : 'bg-blue-50 border-blue-100';
  return (
    <div className={`rounded-md border px-3 py-2 ${sideClass}`}>
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className="text-xs font-bold text-gray-700">
          {senderName || senderRole}
          <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-500">{senderRole}</span>
          {badge && (
            <span className="ml-1.5 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-500">
              {badge}
            </span>
          )}
        </span>
        <span className="text-[10px] text-gray-400 tabular-nums">{createdAt ? new Date(createdAt).toLocaleString() : ''}</span>
      </div>
      <p className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-snug">{text}</p>
    </div>
  );
}
